/**
 * Hook types enum used to identify the type of hook to add in the FunctionIntrusion.
 */
export const HookType = Object.freeze({
  Before: 'before',
  After: 'after',
  Replace: 'replace'
});
const HookTypeValues = new Set(Object.values(HookType));


/**
 * Registry for managing hooks on a single function.
 *
 * Design:
 * - Single wrapper per function with cached creation
 * - Array-based FIFO execution order
 * - Owner-based isolation for multi-actor scenarios
 * - Enable/disable without removing hooks
 * - Automatic cleanup when no hooks remain
 */
export class FunctionHookRegistry {

  #originalFunction;
  #wrapperFunction = undefined; // Cache the wrapper to prevent multiple creation
  #enabled = true; // Allow enabling/disabling hooks without removing them
  #maskedOwners = new Set(); // Track which owners have their hooks disabled

  #hooks = { before: new Array(), after: new Array(), replace: new Array() };

  /**
   * Creates a new hook registry for a function.
   * @param {Function} originalFunction - The original function to hook
   */
  constructor(originalFunction) {
    if (!(originalFunction instanceof Function)) {
      throw new Error('Original function is required and must be a function');
    }
    this.#originalFunction = originalFunction;
  }

  static #verifyHookType(type) {
    if (HookTypeValues.has(type)) return true;
    console.error(`FunctionHookRegistry: Invalid hook type: ${type}`);
    return false;
  }

  /**
   * Adds a hook to the registry.
   * @param {HookType} type - Hook type (use HookType enum)
   * @param {Function} callback - The hook callback function
   * @param {*} owner - The owner object for cleanup purposes
   */
  addHook(type, callback, owner) {
    if (!FunctionHookRegistry.#verifyHookType(type)) return;

    if (!(callback instanceof Function)) {
      console.warn('FunctionHookRegistry: Callback must be a function');
      return;
    }
    const hookEntry = { callback, owner };
    this.#hooks[type].push(hookEntry);
  }

  /**
   * Removes a specific hook from the registry.
   * @param {HookType} type - Hook type (use HookType enum)
   * @param {Function} callback - The hook callback function to remove
   * @param {*} owner - The owner object
   */
  removeHook(type, callback, owner) {
    if (!FunctionHookRegistry.#verifyHookType(type)) return;

    const hooks = this.#hooks[type];
    const indexToRemove = hooks.findIndex((entry) => entry.callback === callback && entry.owner === owner);
    indexToRemove !== -1 && hooks.splice(indexToRemove, 1);
  }

  /**
   * Removes all hooks owned by a specific owner.
   * @param {*} owner - The owner object
   */
  removeHooksByOwner(owner) {
    for (const hooks of Object.values(this.#hooks)) {
      for (let i = hooks.length - 1; i >= 0; --i) {
        hooks[i].owner === owner && hooks.splice(i, 1);
      }
    }
  }

  /**
   * Creates a wrapper function that executes all hooks. Returns cached wrapper if already created.
   */
  createWrapper() {
    if (this.#wrapperFunction) return this.#wrapperFunction;

    const originalFunction = this.#originalFunction;
    const hooks = this.#hooks;
    const registry = this;

    this.#wrapperFunction = function (...args) {
      // Remarks: try/catches are not used to avoid performance overhead and the callbacks are executed in the
      // order they were added (FIFO). If hooks are disabled or no hooks exist, call original function directly.
      if (!registry.#enabled || hooks.before.length === 0 && hooks.after.length === 0 && hooks.replace.length === 0) {
        return originalFunction.call(this, ...args);
      }
      const fnContext = this;

      // Execute before hooks for non-disabled owners only
      for (const { callback, owner } of hooks.before) {
        if (registry.#maskedOwners.has(owner)) continue;
        callback(fnContext, args);
      }

      let result = undefined;
      let wasReplacedAtLeastOnce = false;
      if (hooks.replace.length > 0) {
        // Execute replace hooks in FIFO order for non-disabled owners only. Last enabled replace hook's result is used.
        for (const { callback, owner } of hooks.replace) {
          if (registry.#maskedOwners.has(owner)) continue;
          result = callback(fnContext, args);
          wasReplacedAtLeastOnce ||= true;
        }
      }
      if (!wasReplacedAtLeastOnce) {
        result = originalFunction.call(this, ...args);
      }

      // Execute after hooks for non-disabled owners only
      for (const { callback, owner } of hooks.after) {
        if (registry.#maskedOwners.has(owner)) continue;
        callback(fnContext, args, result);
      }

      return result;
    };
    return this.#wrapperFunction;
  }

  /**
   * Checks if the registry has any hooks.
   * @returns {boolean} True if there are any hooks registered
   */
  hasHooks() {
    return this.#hooks.before.length + this.#hooks.after.length + this.#hooks.replace.length > 0;
  }

  /**
   * Checks if the registry has a wrapper function created.
   * @returns {boolean} True if wrapper has been created
   */
  isWrapped() {
    return this.#wrapperFunction !== undefined;
  }

  getOriginalFunction() {
    return this.#originalFunction;
  }

  enable(owner) {
    this.#maskedOwners.delete(owner);
  }

  disable(owner) {
    this.#maskedOwners.add(owner);
  }

  isEnabled(owner) {
    return !this.#maskedOwners.has(owner);
  }
}